/**
 * useEvents hook -- fetches calendar events from GET /calendar and provides mutation methods.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import { apiClient } from "../lib/api-client";
import type { CalendarEvent, CalendarEventInput } from "@atlas/shared";

export interface UseEventsOptions {
  startDate?: string;
  endDate?: string;
}

export interface UseEventsReturn {
  events: CalendarEvent[];
  isLoading: boolean;
  error: string | null;
  fetchEvents: (start: string, end: string) => Promise<void>;
  refresh: () => Promise<void>;
  createEvent: (input: Omit<CalendarEventInput, "userId">) => Promise<CalendarEvent>;
  updateEvent: (startTime: string, id: string, updates: Partial<Omit<CalendarEventInput, "userId">>) => Promise<CalendarEvent>;
  deleteEvent: (startTime: string, id: string) => Promise<void>;
}

interface CalendarResponse {
  events: CalendarEvent[];
}

interface EventResponse {
  event: CalendarEvent;
}

function formatDate(date: Date): string {
  return date.toISOString().split("T")[0];
}

export function useEvents(apiBaseUrl: string, options?: UseEventsOptions): UseEventsReturn {
  const { startDate: initialStart, endDate: initialEnd } = options ?? {};

  const defaultStart = formatDate(new Date());
  const defaultEnd = formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));

  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const currentRangeRef = useRef({
    start: initialStart ?? defaultStart,
    end: initialEnd ?? defaultEnd,
  });

  const fetchEvents = useCallback(async (start: string, end: string) => {
    setIsLoading(true);
    setError(null);
    currentRangeRef.current = { start, end };

    try {
      const path = `/calendar?startDate=${start}&endDate=${end}`;
      const data = await apiClient<CalendarResponse>(apiBaseUrl, path);
      setEvents(data.events);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl]);

  const refresh = useCallback(async () => {
    const { start, end } = currentRangeRef.current;
    await fetchEvents(start, end);
  }, [fetchEvents]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const start = initialStart ?? defaultStart;
      const end = initialEnd ?? defaultEnd;
      currentRangeRef.current = { start, end };

      setIsLoading(true);
      setError(null);

      try {
        const path = `/calendar?startDate=${start}&endDate=${end}`;
        const data = await apiClient<CalendarResponse>(apiBaseUrl, path);
        if (!cancelled) {
          setEvents(data.events);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, initialStart, initialEnd, defaultStart, defaultEnd]);

  const createEvent = useCallback(async (
    input: Omit<CalendarEventInput, "userId">,
  ): Promise<CalendarEvent> => {
    const data = await apiClient<EventResponse>(apiBaseUrl, "/calendar", {
      method: "POST",
      body: JSON.stringify(input),
    });
    await refresh();
    return data.event;
  }, [apiBaseUrl, refresh]);

  const updateEvent = useCallback(async (
    startTime: string,
    id: string,
    updates: Partial<Omit<CalendarEventInput, "userId">>,
  ): Promise<CalendarEvent> => {
    const encodedStartTime = encodeURIComponent(startTime);
    const data = await apiClient<EventResponse>(
      apiBaseUrl,
      `/calendar/${encodedStartTime}/${id}`,
      { method: "PUT", body: JSON.stringify(updates) },
    );
    await refresh();
    return data.event;
  }, [apiBaseUrl, refresh]);

  const deleteEvent = useCallback(async (startTime: string, id: string): Promise<void> => {
    const encodedStartTime = encodeURIComponent(startTime);
    await apiClient(apiBaseUrl, `/calendar/${encodedStartTime}/${id}`, {
      method: "DELETE",
    });
    await refresh();
  }, [apiBaseUrl, refresh]);

  return { events, isLoading, error, fetchEvents, refresh, createEvent, updateEvent, deleteEvent };
}
